export const basic = {
  black: 'hsla(0, 0%, 0%, 1)',
  white: 'hsla(0, 0%, 100%, 1)',
  red: {
    5: 'hsla(346, 68%, 96%, 1)',
    10: 'hsla(348, 74%, 89%, 1)',
    20: 'hsla(348, 73%, 84%, 1)',
    30: 'hsla(348, 73%, 74%, 1)',
    40: 'hsla(348, 73%, 63%, 1)',
    50: 'hsla(348, 74%, 58%, 1)',
    60: 'hsla(348, 81%, 48%, 1)',
    70: 'hsla(348, 100%, 39%, 1)',
    80: 'hsla(348, 100%, 35%, 1)',
    90: 'hsla(348, 100%, 27%, 1)',
    100: 'hsla(348, 100%, 19%, 1)'
  },
  yellow: {
    5: 'hsla(36, 100%, 94%, 1)',
    10: 'hsla(37, 100%, 87%, 1)',
    20: 'hsla(38, 100%, 70%, 1)',
    30: 'hsla(38, 100%, 57%, 1)',
    40: 'hsla(38, 91%, 47%, 1)',
    50: 'hsla(38, 91%, 38%, 1)',
    60: 'hsla(38, 90%, 29%, 1)',
    70: 'hsla(38, 92%, 24%, 1)',
    80: 'hsla(38, 92%, 19%, 1)',
    90: 'hsla(38, 92%, 14%, 1)',
    100: 'hsla(38, 92%, 9%, 1)'
  },
  green: {
    5: 'hsla(153, 33%, 94%, 1)',
    10: 'hsla(153, 34%, 87%, 1)',
    20: 'hsla(153, 34%, 75%, 1)',
    30: 'hsla(154, 34%, 63%, 1)',
    40: 'hsla(153, 34%, 50%, 1)',
    50: 'hsla(154, 56%, 38%, 1)',
    60: 'hsla(154, 100%, 25%, 1)',
    70: 'hsla(154, 100%, 21%, 1)',
    80: 'hsla(153, 100%, 17%, 1)',
    90: 'hsla(154, 100%, 13%, 1)',
    100: 'hsla(153, 100%, 8%, 1)'
  },
  blue: {
    5: 'hsla(208, 100%, 95%, 1)',
    10: 'hsla(208, 100%, 93%, 1)',
    20: 'hsla(208, 100%, 83%, 1)',
    30: 'hsla(208, 100%, 73%, 1)',
    40: 'hsla(208, 100%, 63%, 1)',
    50: 'hsla(208, 100%, 53%, 1)',
    60: 'hsla(208, 100%, 43%, 1)',
    70: 'hsla(208, 100%, 33%, 1)',
    80: 'hsla(208, 100%, 23%, 1)',
    90: 'hsla(208, 100%, 14%, 1)',
    100: 'hsla(208, 100%, 7%, 1)'
  },
  gray: {
    5: 'hsla(0, 0%, 96%, 1)',
    10: 'hsla(180, 2%, 92%, 1)',
    20: 'hsla(210, 4%, 79%, 1)',
    30: 'hsla(210, 4%, 67%, 1)',
    40: 'hsla(210, 4%, 59%, 1)',
    50: 'hsla(207, 4%, 51%, 1)',
    60: 'hsla(210, 6%, 38%, 1)',
    70: 'hsla(210, 9%, 31%, 1)',
    80: 'hsla(208, 12%, 24%, 1)',
    90: 'hsla(210, 19%, 17%, 1)',
    100: 'hsla(208, 33%, 10%, 1)'
  }
}

export function alpha(hsl: string, alpha: number) {
  return hsl.replace(', 1)', `, ${alpha})`)
}

type BrandPalette = {
  DEFAULT: string
  hover: string
  active: string
  light: {
    DEFAULT: string
    hover: string
    active: string
  }
}

function hsl(hue: number, saturation: number, lightness: number) {
  return `hsla(${Math.round(hue)}, ${Math.round(saturation)}%, ${Math.round(lightness)}%, 1)`
}

function hexToHsl(hex: string) {
  const red = Number.parseInt(hex.slice(1, 3), 16) / 255
  const green = Number.parseInt(hex.slice(3, 5), 16) / 255
  const blue = Number.parseInt(hex.slice(5, 7), 16) / 255
  const maximum = Math.max(red, green, blue)
  const minimum = Math.min(red, green, blue)
  const lightness = (maximum + minimum) / 2
  const difference = maximum - minimum

  if (difference === 0) return { hue: 0, saturation: 0, lightness: lightness * 100 }

  const saturation = difference / (1 - Math.abs(2 * lightness - 1))
  const hue =
    maximum === red
      ? 60 * (((green - blue) / difference) % 6)
      : maximum === green
        ? 60 * ((blue - red) / difference + 2)
        : 60 * ((red - green) / difference + 4)

  return { hue: hue < 0 ? hue + 360 : hue, saturation: saturation * 100, lightness: lightness * 100 }
}

export function brandPalette(primaryColor?: string): BrandPalette {
  if (!primaryColor || !/^#[0-9A-Fa-f]{6}$/.test(primaryColor)) {
    return {
      DEFAULT: basic.blue[60],
      hover: basic.blue[70],
      active: basic.blue[80],
      light: {
        DEFAULT: basic.blue[5],
        hover: basic.blue[10],
        active: basic.blue[20]
      }
    }
  }

  const { hue, saturation, lightness } = hexToHsl(primaryColor)
  return {
    DEFAULT: hsl(hue, saturation, lightness),
    hover: hsl(hue, saturation, Math.max(lightness - 10, 0)),
    active: hsl(hue, saturation, Math.max(lightness - 20, 0)),
    light: {
      DEFAULT: hsl(hue, Math.min(saturation, 45), 95),
      hover: hsl(hue, Math.min(saturation, 50), 90),
      active: hsl(hue, Math.min(saturation, 55), 80)
    }
  }
}

export function createSemantic(primaryColor?: string) {
  const brand = brandPalette(primaryColor)
  return {
  primary: {
    DEFAULT: brand.DEFAULT,
    hover: brand.hover,
    active: brand.active
  },
  'primary-light': {
    DEFAULT: brand.light.DEFAULT,
    hover: brand.light.hover,
    active: brand.light.active
  },
  'primary-subtle': {
    DEFAULT: alpha(brand.DEFAULT, 0.1),
    hover: alpha(brand.DEFAULT, 0.2),
    active: alpha(brand.DEFAULT, 0.3)
  },
  'primary-flat': {
    DEFAULT: alpha(brand.DEFAULT, 0),
    hover: alpha(brand.DEFAULT, 0.1),
    active: alpha(brand.DEFAULT, 0.2)
  },
  success: {
    DEFAULT: basic.green[60],
    hover: basic.green[70],
    active: basic.green[80]
  },
  'success-light': {
    DEFAULT: basic.green[5],
    hover: basic.green[10],
    active: basic.green[20]
  },
  'success-subtle': {
    DEFAULT: alpha(basic.green[60], 0.1),
    hover: alpha(basic.green[60], 0.2),
    active: alpha(basic.green[60], 0.3)
  },
  'success-flat': {
    DEFAULT: alpha(basic.green[60], 0),
    hover: alpha(basic.green[60], 0.1),
    active: alpha(basic.green[60], 0.2)
  },
  danger: {
    DEFAULT: basic.red[60],
    hover: basic.red[70],
    active: basic.red[80]
  },
  'danger-light': {
    DEFAULT: basic.red[5],
    hover: basic.red[10],
    active: basic.red[20]
  },
  'danger-subtle': {
    DEFAULT: alpha(basic.red[60], 0.1),
    hover: alpha(basic.red[60], 0.2),
    active: alpha(basic.red[60], 0.3)
  },
  'danger-flat': {
    DEFAULT: alpha(basic.red[60], 0),
    hover: alpha(basic.red[60], 0.1),
    active: alpha(basic.red[60], 0.2)
  },
  info: {
    DEFAULT: basic.blue[60],
    hover: basic.blue[70],
    active: basic.blue[80]
  },
  'info-light': {
    DEFAULT: basic.blue[5],
    hover: basic.blue[10],
    active: basic.blue[20]
  },
  'info-subtle': {
    DEFAULT: alpha(basic.blue[60], 0.1),
    hover: alpha(basic.blue[60], 0.2),
    active: alpha(basic.blue[60], 0.3)
  },
  'info-flat': {
    DEFAULT: alpha(basic.blue[60], 0),
    hover: alpha(basic.blue[60], 0.1),
    active: alpha(basic.blue[60], 0.2)
  },
  warning: {
    DEFAULT: basic.yellow[30],
    hover: basic.yellow[40],
    active: basic.yellow[50]
  },
  'warning-light': {
    DEFAULT: basic.yellow[5],
    hover: basic.yellow[10],
    active: basic.yellow[20]
  },
  'warning-subtle': {
    DEFAULT: alpha(basic.yellow[30], 0.1),
    hover: alpha(basic.yellow[30], 0.2),
    active: alpha(basic.yellow[30], 0.3)
  },
  'warning-flat': {
    DEFAULT: alpha(basic.yellow[30], 0),
    hover: alpha(basic.yellow[30], 0.1),
    active: alpha(basic.yellow[30], 0.2)
  },
  'fill-light': {
    DEFAULT: basic.gray[5],
    hover: basic.gray[10],
    active: basic.gray[20]
  },
  'fill-subtle': {
    DEFAULT: alpha(basic.gray[100], 0.05),
    hover: alpha(basic.gray[100], 0.1),
    active: alpha(basic.gray[100], 0.15)
  },
  'fill-flat': {
    DEFAULT: alpha(basic.gray[100], 0),
    hover: alpha(basic.gray[100], 0.05),
    active: alpha(basic.gray[100], 0.1)
  },
  background: {
    primary: basic.white,
    secondary: basic.gray[5],
    tertiary: basic.gray[10],
    brand: brand.DEFAULT
  },
  border: {
    10: basic.gray[10],
    20: basic.gray[20],
    30: basic.gray[30],
    40: basic.gray[40],
    focus: brand.DEFAULT,
    danger: basic.red[60]
  },
  text: {
    primary: basic.gray[100],
    secondary: alpha(basic.gray[100], 0.7),
    tertiary: alpha(basic.gray[100], 0.5),
    quaternary: alpha(basic.gray[100], 0.3),
    onColor: basic.white,
    success: basic.green[60],
    'success-emphasis': basic.green[80],
    danger: basic.red[60],
    'danger-emphasis': basic.red[80],
    info: brand.DEFAULT,
    'info-emphasis': brand.active,
    warning: basic.yellow[60],
    'warning-emphasis': basic.yellow[80]
  }
  }
}

export const semantic = createSemantic()
